import React, { useState, useEffect, useMemo } from 'react';
import { getPlanifications, createPlanification, updatePlanification, deletePlanification, getSuggestions, getClients, getCommerciaux, getAbsences, createAbsence, approuverAbsence, refuserAbsence, deleteAbsence } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiCalendar, FiPlus, FiTrash2, FiCheck, FiMapPin, FiUser, FiFilter, FiList, FiGrid, FiChevronLeft, FiChevronRight, FiClock, FiX, FiUserMinus, FiAlertCircle } from 'react-icons/fi';
import SearchableSelect from '../components/SearchableSelect';

const TYPES = ['visite', 'reunion', 'livraison', 'installation', 'autre'];
const PRIORITES = ['basse', 'normale', 'haute', 'urgente'];
const PRIORITE_COLORS: any = { basse: '#94A3B8', normale: '#3B82F6', haute: '#F59E0B', urgente: '#EF4444' };
const TYPE_COLORS: any = { visite: '#16A34A', reunion: '#3B82F6', livraison: '#8B5CF6', installation: '#F97316', autre: '#64748B' };
const TYPE_LABELS: any = { visite: 'Visite', reunion: 'Reunion', livraison: 'Livraison', installation: 'Installation', autre: 'Autre' };
const PRIORITE_LABELS: any = { basse: 'Basse', normale: 'Normale', haute: 'Haute', urgente: 'Urgente' };
const STATUTS = ['planifie', 'en_cours', 'termine', 'annule'];
const STATUT_LABELS: any = { planifie: 'Planifie', en_cours: 'En cours', termine: 'Termine', annule: 'Annule' };
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_LABELS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

// Absence types
const ABS_TYPES = ['conge', 'maladie', 'absence', 'formation'];
const ABS_TYPE_LABELS: any = { conge: 'Conge', maladie: 'Maladie', absence: 'Absence', formation: 'Formation' };
const ABS_TYPE_COLORS: any = { conge: '#16A34A', maladie: '#EF4444', absence: '#F59E0B', formation: '#3B82F6' };
const ABS_STATUT_COLORS: any = { en_attente: '#F59E0B', approuve: '#16A34A', refuse: '#EF4444' };

type ViewMode = 'liste' | 'calendrier' | 'absences';

const Planning: React.FC = () => {
    const { isManager, isAdmin } = useAuth();
    const [plans, setPlans] = useState<any[]>([]);
    const [suggestions, setSuggestions] = useState<any>(null);
    const [clients, setClients] = useState<any[]>([]);
    const [commerciaux, setCommerciaux] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<any>({
        user_id: '', user_ids: [] as string[], client_id: '', titre: '', description: '', type: 'visite',
        date_planifiee: '', duree_minutes: 60, priorite: 'normale', notes: ''
    });
    const [viewDate, setViewDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterUser, setFilterUser] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterPriorite, setFilterPriorite] = useState('');
    const [filterStatut, setFilterStatut] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('calendrier');
    const [showFilters, setShowFilters] = useState(false);

    // Calendar state
    const [calYear, setCalYear] = useState(new Date().getFullYear());
    const [calMonth, setCalMonth] = useState(new Date().getMonth());

    // Absences state
    const [absences, setAbsences] = useState<any[]>([]);
    const [absStats, setAbsStats] = useState<any>({});
    const [showAbsModal, setShowAbsModal] = useState(false);
    const [absForm, setAbsForm] = useState({ user_id: '', type: 'conge', date_debut: '', date_fin: '', motif: '' });
    const [absFilterStatut, setAbsFilterStatut] = useState('');
    const [absFilterType, setAbsFilterType] = useState('');

    useEffect(() => {
        getClients({ limit: 500 }).then(res => {
            const data = res.data.data || res.data;
            setClients(Array.isArray(data) ? data : []);
        });
        getCommerciaux().then(res => {
            const data = res.data.data || res.data;
            setCommerciaux(Array.isArray(data) ? data : []);
        }).catch(() => {});
    }, []);

    const loadPlans = () => {
        let startDate: Date, endDate: Date;
        if (viewMode === 'calendrier') {
            startDate = new Date(calYear, calMonth, 1);
            startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
            endDate = new Date(calYear, calMonth + 1, 0);
            endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
        } else {
            const weekStart = new Date(viewDate);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
            startDate = weekStart;
            endDate = new Date(weekStart);
            endDate.setDate(endDate.getDate() + 6);
        }
        const params: any = { date_debut: startDate.toISOString(), date_fin: endDate.toISOString() };
        if (filterUser) params.user_id = filterUser;
        if (filterType) params.type = filterType;
        if (filterStatut) params.statut = filterStatut;
        getPlanifications(params).then(res => {
            const data = res.data.data || res.data;
            setPlans(Array.isArray(data) ? data : []);
        });
        if (viewMode === 'liste') {
            getSuggestions({ date: viewDate }).then(res => setSuggestions(res.data)).catch(() => {});
        }
    };

    const loadAbsences = () => {
        const params: any = {};
        if (absFilterStatut) params.statut = absFilterStatut;
        if (absFilterType) params.type = absFilterType;
        getAbsences(params).then(res => {
            setAbsences(res.data.data || []);
            setAbsStats(res.data.stats || {});
        }).catch(() => {});
    };

    useEffect(() => { if (viewMode !== 'absences') loadPlans(); }, [viewDate, filterUser, filterType, filterStatut, viewMode, calYear, calMonth]);
    useEffect(() => { loadAbsences(); }, [absFilterStatut, absFilterType]);

    // Also load absences for calendar overlay
    useEffect(() => {
        if (viewMode === 'calendrier') loadAbsences();
    }, [viewMode, calYear, calMonth]);

    const filteredPlans = useMemo(() => {
        let result = plans;
        if (filterPriorite) result = result.filter(p => p.priorite === filterPriorite);
        return result;
    }, [plans, filterPriorite]);

    const activeFiltersCount = [filterUser, filterType, filterPriorite, filterStatut].filter(Boolean).length;
    const clearAllFilters = () => { setFilterUser(''); setFilterType(''); setFilterPriorite(''); setFilterStatut(''); };

    const openNew = (date?: string) => {
        setEditingId(null);
        const d = date || viewDate;
        setForm({
            user_id: '', user_ids: filterUser ? [filterUser] : [], client_id: '', titre: '', description: '', type: 'visite',
            date_planifiee: d + 'T09:00', duree_minutes: 60, priorite: 'normale', notes: ''
        });
        setShowModal(true);
    };

    const openEdit = (p: any) => {
        setEditingId(p.id);
        setForm({
            user_id: p.user_id ? String(p.user_id) : '',
            user_ids: p.user_id ? [String(p.user_id)] : [],
            client_id: p.client_id || '',
            titre: p.titre || '',
            description: p.description || '',
            type: p.type || 'visite',
            date_planifiee: p.date_planifiee ? new Date(p.date_planifiee).toISOString().slice(0, 16) : '',
            duree_minutes: p.duree_minutes || 60,
            priorite: p.priorite || 'normale',
            notes: p.notes || '',
        });
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (editingId) {
            const data = { ...form, client_id: form.client_id || null, user_id: form.user_id || null };
            delete data.user_ids;
            await updatePlanification(editingId, data);
        } else {
            const data = { ...form, client_id: form.client_id || null, user_ids: form.user_ids.length > 0 ? form.user_ids.map(Number) : null };
            delete data.user_id;
            await createPlanification(data);
        }
        setShowModal(false);
        loadPlans();
    };

    const handleStatusChange = async (id: number, statut: string) => { await updatePlanification(id, { statut }); loadPlans(); };
    const handleDelete = async (id: number) => { if (window.confirm('Supprimer cette planification ?')) { await deletePlanification(id); loadPlans(); } };

    // Absence handlers
    const handleAbsCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const resp = await createAbsence(absForm);
            if (resp.data.error) { alert(resp.data.error); return; }
            setShowAbsModal(false);
            setAbsForm({ user_id: '', type: 'conge', date_debut: '', date_fin: '', motif: '' });
            loadAbsences();
        } catch (err: any) { alert(err?.response?.data?.error || 'Erreur lors de la creation'); }
    };
    const handleAbsApprove = async (id: number) => { await approuverAbsence(id); loadAbsences(); };
    const handleAbsReject = async (id: number) => { await refuserAbsence(id); loadAbsences(); };
    const handleAbsDelete = async (id: number) => { if (window.confirm('Supprimer cette demande ?')) { await deleteAbsence(id); loadAbsences(); } };

    // ─── Calendar helpers ───
    const calendarDays = useMemo(() => {
        const firstDay = new Date(calYear, calMonth, 1);
        const lastDay = new Date(calYear, calMonth + 1, 0);
        const startOffset = (firstDay.getDay() + 6) % 7;
        const days: { date: Date; inMonth: boolean }[] = [];
        for (let i = startOffset - 1; i >= 0; i--) { days.push({ date: new Date(calYear, calMonth, -i), inMonth: false }); }
        for (let i = 1; i <= lastDay.getDate(); i++) { days.push({ date: new Date(calYear, calMonth, i), inMonth: true }); }
        while (days.length % 7 !== 0) { days.push({ date: new Date(calYear, calMonth + 1, days.length - lastDay.getDate() - startOffset + 1), inMonth: false }); }
        return days;
    }, [calYear, calMonth]);

    const plansByDate = useMemo(() => {
        const map: Record<string, any[]> = {};
        filteredPlans.forEach(p => {
            const key = new Date(p.date_planifiee).toISOString().split('T')[0];
            if (!map[key]) map[key] = [];
            map[key].push(p);
        });
        Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.date_planifiee).getTime() - new Date(b.date_planifiee).getTime()));
        return map;
    }, [filteredPlans]);

    // Map absences to calendar dates
    const absencesByDate = useMemo(() => {
        const map: Record<string, any[]> = {};
        absences.filter(a => a.statut !== 'refuse').forEach(a => {
            const start = new Date(a.date_debut);
            const end = new Date(a.date_fin);
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const key = d.toISOString().split('T')[0];
                if (!map[key]) map[key] = [];
                map[key].push(a);
            }
        });
        return map;
    }, [absences]);

    const todayStr = new Date().toISOString().split('T')[0];

    const prevMonth = () => { if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1); };
    const nextMonth = () => { if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1); };
    const goToday = () => { const now = new Date(); setCalYear(now.getFullYear()); setCalMonth(now.getMonth()); setViewDate(now.toISOString().split('T')[0]); };

    const groupByDate = (items: any[]) => {
        const groups: any = {};
        items.forEach(item => {
            const date = new Date(item.date_planifiee).toLocaleDateString('fr', { weekday: 'long', day: 'numeric', month: 'long' });
            if (!groups[date]) groups[date] = [];
            groups[date].push(item);
        });
        return groups;
    };
    const grouped = groupByDate(filteredPlans);

    // ─── Filters bar (shared) ───
    const renderFilters = () => (
        <div style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
            padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 14
        }}>
            <div style={{ minWidth: 220, flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Commercial</label>
                <SearchableSelect value={filterUser} onChange={setFilterUser} placeholder="Tous les commerciaux"
                    options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))} />
            </div>
            <div style={{ minWidth: 140 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Type</label>
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, width: '100%', minHeight: 36, background: '#fff' }}>
                    <option value="">Tous</option>
                    {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
            </div>
            <div style={{ minWidth: 140 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Priorite</label>
                <select value={filterPriorite} onChange={e => setFilterPriorite(e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, width: '100%', minHeight: 36, background: '#fff' }}>
                    <option value="">Toutes</option>
                    {PRIORITES.map(p => <option key={p} value={p}>{PRIORITE_LABELS[p]}</option>)}
                </select>
            </div>
            <div style={{ minWidth: 140 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Statut</label>
                <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, width: '100%', minHeight: 36, background: '#fff' }}>
                    <option value="">Tous</option>
                    {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                </select>
            </div>
            {activeFiltersCount > 0 && (
                <div style={{ alignSelf: 'flex-end', paddingBottom: 2 }}>
                    <button onClick={clearAllFilters}
                        style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#EF4444', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiX size={12} /> Effacer ({activeFiltersCount})
                    </button>
                </div>
            )}
        </div>
    );

    // ─── Calendar event pill ───
    const renderCalEvent = (p: any, compact: boolean = false) => {
        const time = new Date(p.date_planifiee).toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' });
        const bgColor = TYPE_COLORS[p.type] || '#64748B';
        const isTermine = p.statut === 'termine';
        const isAnnule = p.statut === 'annule';
        return (
            <div key={p.id} onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                title={`${time} - ${p.titre}${p.client_nom ? ' | ' + p.client_nom : ''}${p.user_nom ? ' | ' + p.user_nom : ''}`}
                style={{
                    padding: compact ? '2px 5px' : '3px 6px', borderRadius: 4,
                    background: isAnnule ? '#f1f5f9' : isTermine ? '#f0fdf4' : `${bgColor}15`,
                    borderLeft: `3px solid ${isAnnule ? '#94A3B8' : isTermine ? '#16A34A' : bgColor}`,
                    fontSize: 11, cursor: 'pointer', marginBottom: 2, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: isAnnule ? '#94A3B8' : '#333', textDecoration: isAnnule ? 'line-through' : 'none',
                    opacity: isAnnule ? 0.6 : 1, transition: 'background 0.15s'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = isAnnule ? '#e2e8f0' : `${bgColor}30`)}
                onMouseLeave={e => (e.currentTarget.style.background = isAnnule ? '#f1f5f9' : isTermine ? '#f0fdf4' : `${bgColor}15`)}>
                <span style={{ fontWeight: 600, marginRight: 4, color: isAnnule ? '#94A3B8' : bgColor }}>{time}</span>
                {compact ? <span style={{ color: '#555' }}>{p.titre}</span> : <>{p.titre}</>}
            </div>
        );
    };

    // ─── Calendar absence pill ───
    const renderCalAbsence = (a: any) => {
        const color = ABS_TYPE_COLORS[a.type] || '#F59E0B';
        const isPending = a.statut === 'en_attente';
        return (
            <div key={`abs-${a.id}`}
                title={`${ABS_TYPE_LABELS[a.type]} - ${a.user_nom}${isPending ? ' (en attente)' : ''}`}
                style={{
                    padding: '2px 5px', borderRadius: 4, fontSize: 10, marginBottom: 2,
                    background: `${color}15`, borderLeft: `3px solid ${color}`,
                    color, fontWeight: 600, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    opacity: isPending ? 0.65 : 1,
                    fontStyle: isPending ? 'italic' : 'normal'
                }}>
                <FiUserMinus size={9} style={{ marginRight: 3 }} />
                {a.user_nom?.split(' ')[0]} - {ABS_TYPE_LABELS[a.type]}
            </div>
        );
    };

    // View toggle button style
    const viewBtn = (mode: ViewMode, label: string, icon: React.ReactNode) => (
        <button key={mode} onClick={() => setViewMode(mode)}
            style={{
                padding: '6px 12px', borderRadius: 6, border: 'none', fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500,
                background: viewMode === mode ? '#fff' : 'transparent',
                color: viewMode === mode ? '#16A34A' : '#64748B',
                boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}>
            {icon} {label}
        </button>
    );

    return (
        <div className="page-planning">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <h2 style={{ margin: 0 }}><FiCalendar /> Planning & Absences</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* View toggle */}
                    <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                        {viewBtn('calendrier', 'Calendrier', <FiGrid size={14} />)}
                        {viewBtn('liste', 'Liste', <FiList size={14} />)}
                        {viewBtn('absences', 'Absences', <FiUserMinus size={14} />)}
                    </div>

                    {viewMode !== 'absences' && (
                        <button onClick={() => setShowFilters(f => !f)}
                            style={{
                                padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500,
                                background: showFilters ? '#f0fdf4' : '#fff', color: showFilters ? '#16A34A' : '#333'
                            }}>
                            <FiFilter size={14} /> Filtres
                            {activeFiltersCount > 0 && (
                                <span style={{ background: '#16A34A', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{activeFiltersCount}</span>
                            )}
                        </button>
                    )}

                    {viewMode !== 'absences' ? (
                        <button className="btn btn-primary" onClick={() => openNew()} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FiPlus /> Planifier
                        </button>
                    ) : (
                        <button className="btn btn-primary" onClick={() => setShowAbsModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FiPlus /> Nouvelle absence
                        </button>
                    )}
                </div>
            </div>

            {/* Filters panel (planning only) */}
            {viewMode !== 'absences' && showFilters && renderFilters()}

            {/* ═══ CALENDAR VIEW ═══ */}
            {viewMode === 'calendrier' && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button onClick={prevMonth} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex' }}><FiChevronLeft size={16} /></button>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, minWidth: 180, textAlign: 'center' }}>{MOIS_LABELS[calMonth]} {calYear}</h3>
                            <button onClick={nextMonth} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex' }}><FiChevronRight size={16} /></button>
                            <button onClick={goToday} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#16A34A', marginLeft: 4 }}>Aujourd'hui</button>
                        </div>
                        <div style={{ fontSize: 13, color: '#64748B' }}>
                            {filteredPlans.length} planification{filteredPlans.length > 1 ? 's' : ''}
                        </div>
                    </div>

                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e5e7eb' }}>
                            {JOURS.map(j => (
                                <div key={j} style={{ padding: '8px 0', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#64748B', background: '#f8fafc', borderRight: '1px solid #f3f4f6' }}>{j}</div>
                            ))}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                            {calendarDays.map((day, idx) => {
                                const dateStr = day.date.toISOString().split('T')[0];
                                const dayPlans = plansByDate[dateStr] || [];
                                const dayAbsences = absencesByDate[dateStr] || [];
                                const isToday = dateStr === todayStr;
                                const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                                const hasAbsence = dayAbsences.length > 0;

                                return (
                                    <div key={idx} onClick={() => openNew(dateStr)}
                                        style={{
                                            minHeight: 100, padding: '4px 5px',
                                            borderRight: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6',
                                            background: hasAbsence && day.inMonth ? '#fef2f2' : isToday ? '#f0fdf4' : isWeekend && day.inMonth ? '#fafafa' : !day.inMonth ? '#f9fafb' : '#fff',
                                            cursor: 'pointer', transition: 'background 0.15s'
                                        }}
                                        onMouseEnter={e => { if (!isToday && !hasAbsence) e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = hasAbsence && day.inMonth ? '#fef2f2' : isToday ? '#f0fdf4' : isWeekend && day.inMonth ? '#fafafa' : !day.inMonth ? '#f9fafb' : '#fff'; }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                            <span style={{
                                                fontSize: 12, fontWeight: isToday ? 700 : day.inMonth ? 500 : 400,
                                                color: isToday ? '#fff' : day.inMonth ? '#333' : '#ccc',
                                                background: isToday ? '#16A34A' : 'transparent',
                                                borderRadius: '50%', width: isToday ? 24 : 'auto', height: isToday ? 24 : 'auto',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>{day.date.getDate()}</span>
                                            {(dayPlans.length + dayAbsences.length) > 0 && (
                                                <span style={{ fontSize: 10, color: '#999', fontWeight: 500 }}>{dayPlans.length + dayAbsences.length}</span>
                                            )}
                                        </div>
                                        <div style={{ maxHeight: 72, overflowY: 'auto' }}>
                                            {/* Absences first */}
                                            {dayAbsences.filter((a: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === a.id) === i).slice(0, 2).map((a: any) => renderCalAbsence(a))}
                                            {/* Planning events */}
                                            {dayPlans.slice(0, 3 - Math.min(dayAbsences.length, 2)).map(p => renderCalEvent(p, true))}
                                            {(dayPlans.length + dayAbsences.length) > 3 && (
                                                <div style={{ fontSize: 10, color: '#3B82F6', fontWeight: 600, padding: '2px 4px' }}>
                                                    +{dayPlans.length + dayAbsences.length - 3} de plus
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Legend */}
                    <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {TYPES.map(t => (
                            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#666' }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, background: TYPE_COLORS[t] }} />{TYPE_LABELS[t]}
                            </div>
                        ))}
                        <div style={{ width: 1, height: 14, background: '#ddd' }} />
                        {ABS_TYPES.map(t => (
                            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#666' }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, background: ABS_TYPE_COLORS[t] }} />
                                <FiUserMinus size={9} /> {ABS_TYPE_LABELS[t]}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ═══ LIST VIEW ═══ */}
            {viewMode === 'liste' && (
                <>
                    <div className="filters-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                        <input type="date" value={viewDate} onChange={e => setViewDate(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
                        <span style={{ fontSize: 13, color: '#64748B' }}>{filteredPlans.length} planification{filteredPlans.length > 1 ? 's' : ''} cette semaine</span>
                    </div>
                    <div className={`planning-grid${suggestions && suggestions.suggestions?.length > 0 ? ' has-sidebar' : ''}`}>
                        <div className="planning-main">
                            {Object.entries(grouped).length === 0 ? <p className="text-muted" style={{ padding: 20 }}>Aucune planification cette semaine</p> :
                                Object.entries(grouped).map(([date, items]: [string, any]) => (
                                    <div key={date} className="planning-day">
                                        <h3 className="day-header">{date}</h3>
                                        {items.map((p: any) => (
                                            <div key={p.id} className="plan-item" style={{ borderLeftColor: PRIORITE_COLORS[p.priorite], cursor: 'pointer' }} onClick={() => openEdit(p)}>
                                                <div className="plan-time">{new Date(p.date_planifiee).toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })}</div>
                                                <div className="plan-content">
                                                    <strong>{p.titre}</strong>
                                                    {p.client_nom && <span className="text-muted"> - {p.client_nom}</span>}
                                                    {p.user_nom && (
                                                        <span style={{ marginLeft: 8, fontSize: 12, color: '#3B82F6', background: '#EFF6FF', padding: '2px 6px', borderRadius: 4 }}>
                                                            <FiUser size={11} style={{ marginRight: 2 }} />{p.user_prenom || ''} {p.user_nom}
                                                        </span>
                                                    )}
                                                    <div className="plan-meta">
                                                        <span className="badge" style={{ background: `${TYPE_COLORS[p.type]}20`, color: TYPE_COLORS[p.type], fontWeight: 600 }}>{TYPE_LABELS[p.type] || p.type}</span>
                                                        <span className="badge" style={{ backgroundColor: PRIORITE_COLORS[p.priorite], color: 'white' }}>{PRIORITE_LABELS[p.priorite] || p.priorite}</span>
                                                        <span style={{ fontSize: 12, color: '#888' }}><FiClock size={11} style={{ marginRight: 2 }} />{p.duree_minutes} min</span>
                                                        <span className={`status ${p.statut}`}>{STATUT_LABELS[p.statut] || p.statut}</span>
                                                    </div>
                                                </div>
                                                <div className="plan-actions" onClick={e => e.stopPropagation()}>
                                                    {p.statut === 'planifie' && <button className="btn-icon" onClick={() => handleStatusChange(p.id, 'termine')} title="Terminer"><FiCheck color="#16A34A" /></button>}
                                                    <button className="btn-icon danger" onClick={() => handleDelete(p.id)} title="Supprimer"><FiTrash2 /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            }
                        </div>
                        {suggestions && suggestions.suggestions?.length > 0 && (
                            <div className="planning-sidebar">
                                <h3><FiMapPin /> Clients a proximite</h3>
                                <div className="suggestions-list">
                                    {suggestions.suggestions.map((s: any) => (
                                        <div key={s.id} className="suggestion-item">
                                            <strong>{s.raison_sociale}</strong>
                                            <span className="text-muted">{Math.round(s.distance)}m</span>
                                            <span className="badge">{s.potentiel}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ═══ ABSENCES VIEW ═══ */}
            {viewMode === 'absences' && (
                <div>
                    {/* KPI Stats */}
                    <div className="bonus-kpi-grid" style={{ marginBottom: 16 }}>
                        {[
                            { label: 'Total demandes', value: absStats.total || 0, color: '#3B82F6', bg: '#dbeafe', icon: <FiCalendar size={18} /> },
                            { label: 'En attente', value: absStats.en_attente || 0, color: '#F59E0B', bg: '#fef3c7', icon: <FiClock size={18} /> },
                            { label: 'Approuvees', value: absStats.approuvees || 0, color: '#16A34A', bg: '#dcfce7', icon: <FiCheck size={18} /> },
                            { label: 'Jours d\'absence', value: `${absStats.total_jours_approuves || 0} j`, color: '#EF4444', bg: '#fee2e2', icon: <FiUserMinus size={18} /> },
                        ].map((k, i) => (
                            <div key={i} className="bonus-kpi-card">
                                <style>{`.bonus-kpi-card:nth-child(${i + 1})::before { background: ${k.color}; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: k.bg, color: k.color }}>{k.icon}</div>
                                    <span className="bk-label">{k.label}</span>
                                </div>
                                <div className="bk-value">{k.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Absence Filters */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                        <FiFilter size={15} color="var(--text-muted)" />
                        <select value={absFilterStatut} onChange={e => setAbsFilterStatut(e.target.value)}
                            style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }}>
                            <option value="">Tous les statuts</option>
                            <option value="en_attente">En attente</option>
                            <option value="approuve">Approuve</option>
                            <option value="refuse">Refuse</option>
                        </select>
                        <select value={absFilterType} onChange={e => setAbsFilterType(e.target.value)}
                            style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }}>
                            <option value="">Tous les types</option>
                            {ABS_TYPES.map(t => <option key={t} value={t}>{ABS_TYPE_LABELS[t]}</option>)}
                        </select>
                    </div>

                    {/* Absences Table */}
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Commercial</th>
                                    <th>Type</th>
                                    <th>Debut</th>
                                    <th>Fin</th>
                                    <th>Jours</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                    <th>Approuve par</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {absences.length === 0 ? (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune absence enregistree</td></tr>
                                ) : absences.map((a: any) => (
                                    <tr key={a.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <FiUser size={14} />
                                                <strong>{a.user_nom}</strong>
                                            </div>
                                            {a.matricule && <small style={{ color: '#999' }}>{a.matricule}</small>}
                                        </td>
                                        <td>
                                            <span style={{
                                                background: `${ABS_TYPE_COLORS[a.type] || '#999'}15`, color: ABS_TYPE_COLORS[a.type] || '#999',
                                                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600
                                            }}>{ABS_TYPE_LABELS[a.type] || a.type}</span>
                                        </td>
                                        <td>{new Date(a.date_debut).toLocaleDateString('fr-FR')}</td>
                                        <td>{new Date(a.date_fin).toLocaleDateString('fr-FR')}</td>
                                        <td><strong>{a.nb_jours}j</strong></td>
                                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {a.motif || <span style={{ color: '#ccc' }}>--</span>}
                                        </td>
                                        <td>
                                            <span style={{
                                                background: `${ABS_STATUT_COLORS[a.statut] || '#999'}15`, color: ABS_STATUT_COLORS[a.statut] || '#999',
                                                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                                                display: 'inline-flex', alignItems: 'center', gap: 4
                                            }}>
                                                {a.statut === 'en_attente' && <FiClock size={11} />}
                                                {a.statut === 'approuve' && <FiCheck size={11} />}
                                                {a.statut === 'refuse' && <FiX size={11} />}
                                                {a.statut === 'en_attente' ? 'En attente' : a.statut === 'approuve' ? 'Approuve' : 'Refuse'}
                                            </span>
                                        </td>
                                        <td>{a.approuve_par_nom || '--'}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                {a.statut === 'en_attente' && (isManager() || isAdmin()) && (
                                                    <>
                                                        <button className="btn-icon" onClick={() => handleAbsApprove(a.id)} title="Approuver"><FiCheck color="#16A34A" /></button>
                                                        <button className="btn-icon" onClick={() => handleAbsReject(a.id)} title="Refuser"><FiX color="#EF4444" /></button>
                                                    </>
                                                )}
                                                {a.statut === 'en_attente' && (
                                                    <button className="btn-icon" onClick={() => handleAbsDelete(a.id)} title="Supprimer"><FiTrash2 color="#999" /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══ PLANNING MODAL ═══ */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>{editingId ? 'Modifier la planification' : 'Nouvelle Planification'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>{editingId ? 'Commercial (assigner a)' : 'Commerciaux (assigner a)'}</label>
                                {editingId ? (
                                    <SearchableSelect value={form.user_id} onChange={(v: string) => setForm({ ...form, user_id: v })} placeholder="-- Moi-meme --"
                                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))} />
                                ) : (
                                    <SearchableSelect multiple value={form.user_ids} onChange={(v: string[]) => setForm({ ...form, user_ids: v })} placeholder="-- Moi-meme --"
                                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))} />
                                )}
                            </div>
                            <div className="form-group"><label>Titre *</label><input value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} required /></div>
                            <div className="form-group">
                                <label>Client</label>
                                <SearchableSelect value={form.client_id ? String(form.client_id) : ''} onChange={(v: string) => setForm({ ...form, client_id: v })} placeholder="Rechercher un client..."
                                    options={clients.map((c: any) => ({ value: String(c.id), label: c.raison_sociale, sub: c.nom_contact || '' }))} />
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Date/Heure *</label><input type="datetime-local" value={form.date_planifiee} onChange={e => setForm({ ...form, date_planifiee: e.target.value })} required /></div>
                                <div className="form-group"><label>Duree (min)</label><input type="number" value={form.duree_minutes} onChange={e => setForm({ ...form, duree_minutes: Number(e.target.value) })} /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Type</label>
                                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                        {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Priorite</label>
                                    <select value={form.priorite} onChange={e => setForm({ ...form, priorite: e.target.value })}>
                                        {PRIORITES.map(p => <option key={p} value={p}>{PRIORITE_LABELS[p] || p}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
                            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editingId ? 'Modifier' : 'Planifier'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═══ ABSENCE MODAL ═══ */}
            {showAbsModal && (
                <div className="modal-overlay" onClick={() => setShowAbsModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3><FiUserMinus style={{ marginRight: 6 }} /> Nouvelle Absence / Conge</h3>
                        <form onSubmit={handleAbsCreate}>
                            {(isManager() || isAdmin()) && (
                                <div className="form-group">
                                    <label>Commercial</label>
                                    <SearchableSelect value={absForm.user_id} onChange={(v: string) => setAbsForm({ ...absForm, user_id: v })} placeholder="Selectionner un commercial"
                                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))} />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Type</label>
                                <select value={absForm.type} onChange={e => setAbsForm({ ...absForm, type: e.target.value })}>
                                    {ABS_TYPES.map(t => <option key={t} value={t}>{ABS_TYPE_LABELS[t]}</option>)}
                                </select>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Date debut</label><input type="date" value={absForm.date_debut} onChange={e => setAbsForm({ ...absForm, date_debut: e.target.value })} required /></div>
                                <div className="form-group"><label>Date fin</label><input type="date" value={absForm.date_fin} onChange={e => setAbsForm({ ...absForm, date_fin: e.target.value })} required /></div>
                            </div>
                            {absForm.date_debut && absForm.date_fin && new Date(absForm.date_fin) >= new Date(absForm.date_debut) && (
                                <div style={{ background: '#f0fdf4', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#16A34A', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiAlertCircle size={13} /> Duree: <strong>{Math.ceil((new Date(absForm.date_fin).getTime() - new Date(absForm.date_debut).getTime()) / 86400000) + 1} jour(s)</strong>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Motif</label>
                                <textarea value={absForm.motif} onChange={e => setAbsForm({ ...absForm, motif: e.target.value })} rows={3} placeholder="Motif de l'absence..." />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAbsModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">Creer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Planning;
